import { useEffect, useRef, useState } from 'react';
import { getYouTubeVideoId, loadYouTubeIframeAPI } from '../utils/youtubePlayer';

const INITIAL_STATE = { status: 'idle', ready: false, playPending: false, currentTime: 0, duration: 0, volume: 80 };
const PLAYER_STATES = { '-1': 'cued', 0: 'ended', 1: 'playing', 2: 'paused', 3: 'buffering', 5: 'cued' };
const seconds = value => Number.isFinite(value) ? Math.max(0, value) : 0;

function useYouTubePlayer({ containerRef, videoUrl, title, enabled, action = 'select', requestId = 0 }) {
  const videoId = getYouTubeVideoId(videoUrl);
  const [state, setState] = useState(INITIAL_STATE);
  const [retryCount, setRetryCount] = useState(0);
  const playerRef = useRef(null);
  const readyRef = useRef(false);
  const statusRef = useRef('idle');
  const pendingPlay = useRef(false);
  const volumeRef = useRef(80);
  const activeVideoRef = useRef('');
  const commandRef = useRef(null);
  const controlsRef = useRef(null);
  const latestRef = useRef(null);
  latestRef.current = { videoId, title, action, requestId };

  // Lifecycle depends on a selection existing, not on expanding or changing tracks.
  useEffect(() => {
    if (!enabled || !containerRef.current) {
      readyRef.current = false;
      pendingPlay.current = false;
      statusRef.current = 'idle';
      setState({ ...INITIAL_STATE, volume: volumeRef.current });
      return;
    }
    let cancelled = false;
    let player;
    let interval;
    let readyTimeout;
    let playTimeout;
    let playAttempts = 0;
    let appliedRequest;
    const container = containerRef.current;
    statusRef.current = 'loading';
    setState({ ...INITIAL_STATE, status: 'loading', playPending: latestRef.current.action === 'play', volume: volumeRef.current });

    const updateStatus = status => {
      statusRef.current = status;
      setState(prev => ({ ...prev, status }));
    };
    const clearPending = () => {
      pendingPlay.current = false;
      clearTimeout(playTimeout);
      playTimeout = undefined;
      setState(prev => ({ ...prev, playPending: false }));
    };
    const sample = () => {
      if (cancelled || !readyRef.current) return;
      const currentTime = seconds(player.getCurrentTime());
      const duration = seconds(player.getDuration());
      const reportedVolume = player.getVolume();
      const volume = player.isMuted() ? 0 : Number.isFinite(reportedVolume) ? reportedVolume : volumeRef.current;
      volumeRef.current = volume;
      setState(prev => ({ ...prev, currentTime, duration, volume }));
    };
    const watchPendingPlay = () => {
      if (pendingPlay.current && !playTimeout) {
        playTimeout = setTimeout(() => {
          if (cancelled || !pendingPlay.current) return;
          clearPending();
          player.pauseVideo();
          updateStatus('blocked');
        }, 6000);
      }
    };
    const flushPendingPlay = () => {
      if (cancelled || !readyRef.current || !pendingPlay.current || playAttempts >= 2) return;
      playAttempts += 1;
      // onReady can precede the initial video's cued event. Keep the request
      // until playback is confirmed, and allow that event one more attempt.
      player.playVideo();
      watchPendingPlay();
    };
    const requestPlay = () => {
      pendingPlay.current = true;
      playAttempts = 0;
      clearTimeout(playTimeout);
      playTimeout = undefined;
      setState(prev => ({ ...prev, playPending: true }));
      flushPendingPlay();
    };
    const pause = () => {
      clearPending();
      if (readyRef.current) player.pauseVideo();
      updateStatus('paused');
    };
    const applyCommand = () => {
      if (cancelled || !readyRef.current) return;
      const command = latestRef.current;
      if (appliedRequest === command.requestId) return;
      appliedRequest = command.requestId;
      player.getIframe().setAttribute('title', command.title);
      if (command.action === 'pause') {
        if (activeVideoRef.current !== command.videoId) {
          clearPending();
          activeVideoRef.current = command.videoId;
          player.cueVideoById({ videoId: command.videoId, startSeconds: 0 });
        }
        pause();
        return;
      }
      if (activeVideoRef.current === command.videoId) {
        if (command.action === 'play') requestPlay();
        return;
      }
      const shouldPlay = command.action === 'play' || ['playing', 'buffering'].includes(statusRef.current);
      clearPending();
      activeVideoRef.current = command.videoId;
      playAttempts = 0;
      playTimeout = undefined;
      pendingPlay.current = shouldPlay;
      updateStatus(shouldPlay ? 'loading' : 'cued');
      setState(prev => ({ ...prev, currentTime: 0, duration: 0, playPending: shouldPlay }));
      if (shouldPlay) {
        // Loading and playing the new ID is atomic. Calling playVideo straight
        // after cueVideoById could briefly replay the previous track instead.
        player.loadVideoById({ videoId: command.videoId, startSeconds: 0 });
        watchPendingPlay();
      } else {
        player.cueVideoById({ videoId: command.videoId, startSeconds: 0 });
      }
    };
    const fail = () => {
      if (cancelled) return;
      clearTimeout(readyTimeout);
      clearInterval(interval);
      clearPending();
      readyRef.current = false;
      updateStatus('error');
      setState(prev => ({ ...prev, ready: false }));
    };
    commandRef.current = applyCommand;
    controlsRef.current = { play: requestPlay, pause };

    loadYouTubeIframeAPI().then(YT => {
      if (cancelled) return;
      const mount = document.createElement('div');
      container.appendChild(mount);
      readyTimeout = setTimeout(fail, 20000);
      activeVideoRef.current = latestRef.current.videoId;
      player = new YT.Player(mount, {
        width: '100%', height: '100%', videoId: latestRef.current.videoId,
        playerVars: { autoplay: 0, playsinline: 1, rel: 0, controls: 1, enablejsapi: 1, origin: window.location.origin },
        events: {
          onReady: event => {
            if (cancelled) return;
            player = event.target;
            playerRef.current = player;
            readyRef.current = true;
            clearTimeout(readyTimeout);
            player.setVolume(volumeRef.current);
            setState(prev => ({ ...prev, ready: true }));
            updateStatus('cued');
            applyCommand();
            sample();
            interval = setInterval(sample, 250);
          },
          onStateChange: event => {
            if (cancelled || !readyRef.current) return;
            const status = PLAYER_STATES[event.data] || 'cued';
            updateStatus(status);
            // Initial unstarted/cued events must not discard a queued play.
            if (event.data === 1 || event.data === 0) clearPending();
            if (event.data === 5) flushPendingPlay();
            sample();
          },
          onAutoplayBlocked: () => {
            if (cancelled) return;
            clearPending();
            updateStatus('blocked');
          },
          onError: fail,
        },
      });
      playerRef.current = player;
      const iframe = player.getIframe();
      iframe.setAttribute('title', latestRef.current.title);
      iframe.setAttribute('allow', 'autoplay; encrypted-media; picture-in-picture');
    }).catch(fail);

    return () => {
      cancelled = true;
      clearTimeout(readyTimeout);
      clearTimeout(playTimeout);
      clearInterval(interval);
      pendingPlay.current = false;
      if (readyRef.current && player) player.pauseVideo();
      readyRef.current = false;
      playerRef.current = null;
      activeVideoRef.current = '';
      controlsRef.current = null;
      commandRef.current = null;
      player?.destroy();
      container.replaceChildren();
    };
  }, [enabled, retryCount, containerRef]);

  useEffect(() => {
    if (!enabled) return;
    if (readyRef.current) commandRef.current?.();
    else {
      // A failed video must not prevent the next preview from creating a fresh player.
      if (statusRef.current === 'error' && videoId !== activeVideoRef.current) {
        setRetryCount(prev => prev + 1);
      }
      pendingPlay.current = action === 'play';
      setState(prev => ({ ...prev, currentTime: 0, duration: 0, playPending: action === 'play' }));
    }
  }, [videoId, action, requestId, enabled]);

  useEffect(() => {
    if (readyRef.current) playerRef.current.getIframe().setAttribute('title', title);
  }, [title]);

  const seek = value => {
    if (!readyRef.current || !state.duration) return;
    const currentTime = Math.max(0, Math.min(state.duration, value));
    playerRef.current.seekTo(currentTime, true);
    setState(prev => ({ ...prev, currentTime }));
  };
  const setVolume = value => {
    const volume = Math.max(0, Math.min(100, value));
    volumeRef.current = volume;
    if (readyRef.current) {
      playerRef.current.setVolume(volume);
      if (volume > 0) playerRef.current.unMute();
    }
    setState(prev => ({ ...prev, volume }));
  };
  return {
    ...state, isPlaying: state.status === 'playing' || state.status === 'buffering', seek, setVolume,
    play: () => { pendingPlay.current = true; controlsRef.current?.play(); },
    pause: () => { pendingPlay.current = false; controlsRef.current?.pause(); },
    retry: () => { pendingPlay.current = true; setRetryCount(prev => prev + 1); },
  };
}

export default useYouTubePlayer;
